import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowLeft, Plus, Pencil, Trash2, LogOut, FileText, BookOpen, Users, Image, X, Upload, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar-chart-style card for analytics
const AnalyticsCard = ({ title, rows }) => {
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <h3 className="font-semibold text-gray-900 mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">No data yet</p>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.label} className="flex items-center gap-3">
              <span className="text-sm text-gray-700 w-44 truncate" title={r.label}>{r.label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                <div className="bg-emerald-500 h-5 rounded-full" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
              <span className="text-sm font-medium text-gray-700 w-10 text-right">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [activeTab, setActiveTab] = useState("blog");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogForm, setBlogForm] = useState({ title: "", category: "Facebook Post", content: "", author: "Sheila", image_ids: [], facebook_url: "" });
  const [editingBlog, setEditingBlog] = useState(null);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importResult, setImportResult] = useState("");
  const [importing, setImporting] = useState(false);

  // Resource state
  const [resources, setResources] = useState([]);
  const [resourceForm, setResourceForm] = useState({ title: "", category: "General", description: "", link: "" });
  const [editingResource, setEditingResource] = useState(null);
  const [showResourceForm, setShowResourceForm] = useState(false);

  // Inquiry state
  const [inquiries, setInquiries] = useState([]);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);

  // Check existing session on mount
  useEffect(() => {
    axios.get(`${API}/admin/me`)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false))
      .finally(() => setAuthChecking(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/login`, loginForm);
      setAuthed(true);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    }
    setIsLoading(false);
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/admin/logout`); } catch { /* ignore */ }
    setAuthed(false);
  };

  const fetchBlog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blog`);
      setBlogPosts(data);
    } catch { setBlogPosts([]); }
  }, []);

  const fetchResources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/resources`);
      setResources(data);
    } catch { setResources([]); }
  }, []);

  const fetchInquiries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/inquiries`);
      setInquiries(data);
    } catch { setInquiries([]); }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/analytics?days=30`);
      setAnalytics(data);
    } catch { setAnalytics(null); }
  }, []);

  useEffect(() => {
    if (authed) {
      fetchBlog();
      fetchResources();
      fetchInquiries();
      fetchAnalytics();
    }
  }, [authed, fetchBlog, fetchResources, fetchInquiries, fetchAnalytics]);

  // Image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingImages(true);
    const newIds = [...blogForm.image_ids];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await axios.post(`${API}/admin/upload-image`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        newIds.push(data.id);
      } catch (err) {
        alert(`Error uploading ${file.name}`);
      }
    }
    setBlogForm({ ...blogForm, image_ids: newIds });
    setUploadingImages(false);
  };

  const removeImage = (idx) => {
    const updated = blogForm.image_ids.filter((_, i) => i !== idx);
    setBlogForm({ ...blogForm, image_ids: updated });
  };

  // Blog CRUD
  const saveBlog = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await axios.put(`${API}/admin/blog/${editingBlog}`, blogForm);
      } else {
        await axios.post(`${API}/admin/blog`, blogForm);
      }
      setBlogForm({ title: "", category: "Facebook Post", content: "", author: "Sheila", image_ids: [], facebook_url: "" });
      setEditingBlog(null);
      setShowBlogForm(false);
      fetchBlog();
    } catch (err) {
      alert("Error saving blog post");
    }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`);
      fetchBlog();
    } catch { alert("Error deleting"); }
  };

  const editBlog = (post) => {
    setBlogForm({ title: post.title, category: post.category, content: post.content, author: post.author, image_ids: post.image_ids || [], facebook_url: post.facebook_url || "" });
    setEditingBlog(post.id);
    setShowBlogForm(true);
  };

  const runImport = async () => {
    setImportResult("");
    let parsed;
    try {
      parsed = JSON.parse(importJson);
    } catch {
      setImportResult("Invalid JSON — please check the syntax.");
      return;
    }
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/admin/blog/import`, parsed);
      setImportResult(`Imported ${data.imported} new post(s). Skipped ${data.skipped} duplicate(s).`);
      fetchBlog();
    } catch (err) {
      setImportResult(`Error: ${err.response?.data?.detail || err.message}`);
    }
    setImporting(false);
  };

  const restoreFromBundle = async () => {
    setImportResult("");
    setImporting(true);
    try {
      const bundle = await fetch("/blog-restore.json").then(r => r.json());
      const { data } = await axios.post(`${API}/admin/blog/import`, bundle);
      setImportResult(`Restored! Imported ${data.imported} post(s). Skipped ${data.skipped} duplicate(s).`);
      fetchBlog();
    } catch (err) {
      setImportResult(`Restore failed: ${err.response?.data?.detail || err.message}`);
    }
    setImporting(false);
  };

  // Resource CRUD
  const saveResource = async (e) => {
    e.preventDefault();
    try {
      if (editingResource) {
        await axios.put(`${API}/admin/resources/${editingResource}`, resourceForm);
      } else {
        await axios.post(`${API}/admin/resources`, resourceForm);
      }
      setResourceForm({ title: "", category: "General", description: "", link: "" });
      setEditingResource(null);
      setShowResourceForm(false);
      fetchResources();
    } catch { alert("Error saving resource"); }
  };

  const deleteResource = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await axios.delete(`${API}/admin/resources/${id}`);
      fetchResources();
    } catch { alert("Error deleting"); }
  };

  const editResource = (item) => {
    setResourceForm({ title: item.title, category: item.category, description: item.description, link: item.link || "" });
    setEditingResource(item.id);
    setShowResourceForm(true);
  };

  // Inquiry status update
  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.put(`${API}/inquiries/${id}/status`, { status });
      fetchInquiries();
    } catch { alert("Error updating status"); }
  };

  const getStatusBadgeClasses = (status) => {
    if (status === "Completed") return "bg-emerald-100 text-emerald-700";
    if (status === "Contacted") return "bg-amber-100 text-amber-700";
    return "bg-gray-100 text-gray-700";
  };

  // ──── Loading Splash ────
  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Checking session…</p>
      </div>
    );
  }

  // ──── Login Screen ────
  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-amber-50 flex items-center justify-center p-4" data-testid="admin-login-page">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md border border-amber-100">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Pathways to Prosperity</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" required data-testid="admin-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" required data-testid="admin-password-input" />
            </div>
            {loginError && <p className="text-red-600 text-sm" data-testid="login-error">{loginError}</p>}
            <button type="submit" disabled={isLoading} className="w-full py-3 bg-emerald-700 text-white font-semibold rounded-xl hover:bg-emerald-800 transition-colors disabled:opacity-50" data-testid="admin-login-btn">
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-emerald-700 hover:underline inline-flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back to Website</a>
          </div>
        </div>
      </div>
    );
  }

  // ──── Dashboard ────
  const blogCategories = ["Facebook Post", "General", "Scholarships", "Advice", "Accessibility", "Career Planning", "News"];
  const resourceCategories = ["General", "Financial Aid", "Scholarships", "Applications", "Accessibility", "Career Planning", "Workforce"];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="font-display text-xl font-bold text-gray-900">Pathways to Prosperity — Admin</h1>
          <div className="flex items-center gap-4">
            <a href="/" className="text-emerald-700 hover:underline text-sm inline-flex items-center gap-1"><ArrowLeft size={16} /> View Site</a>
            <button onClick={handleLogout} className="text-gray-500 hover:text-red-600 inline-flex items-center gap-1 text-sm" data-testid="admin-logout-btn"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-2 mb-8 border-b border-gray-200">
          {[
            { key: "blog", label: "Blog Posts", icon: FileText },
            { key: "resources", label: "Free Resources", icon: BookOpen },
            { key: "inquiries", label: "Student Inquiries", icon: Users },
            { key: "analytics", label: "Site Analytics", icon: BarChart3 }
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)} className={`px-5 py-3 font-medium text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${activeTab === key ? "border-emerald-700 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-700"}`} data-testid={`tab-${key}`}>
              <Icon size={18} /> {label}
              {key === "inquiries" && inquiries.length > 0 && (
                <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">{inquiries.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Blog Tab */}
        {activeTab === "blog" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Blog Posts</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowImportForm(true)}
                  className="border border-emerald-700 text-emerald-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-50 inline-flex items-center gap-2"
                  data-testid="import-blog-btn"
                >
                  <Upload size={18} /> Import JSON
                </button>
                <button onClick={() => { setShowBlogForm(true); setEditingBlog(null); setBlogForm({ title: "", category: "Facebook Post", content: "", author: "Sheila", image_ids: [], facebook_url: "" }); }} className="bg-emerald-700 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-800 inline-flex items-center gap-2" data-testid="add-blog-btn">
                  <Plus size={18} /> New Blog Post
                </button>
              </div>
            </div>

            {showImportForm && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="blog-import-form">
                <h3 className="font-bold text-lg mb-2">Bulk Import Blog Posts</h3>

                {/* Quick Restore */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="font-semibold text-gray-900 text-sm mb-1">⚡ Quick Restore</p>
                  <p className="text-gray-700 text-xs mb-3">Restore the 3 original Facebook blog posts (Sarah Wells tribute, Rising Sophomores, We Are OPEN) bundled with this deploy. Safe to click — duplicates are skipped.</p>
                  <button
                    type="button"
                    onClick={restoreFromBundle}
                    disabled={importing}
                    className="bg-amber-500 text-white px-5 py-2 rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50 text-sm"
                    data-testid="blog-restore-bundle"
                  >
                    {importing ? "Restoring…" : "Restore 3 Posts from Bundle"}
                  </button>
                </div>

                <p className="text-gray-500 text-sm mb-2">Or paste a JSON array of posts manually. Existing posts with the same id are skipped.</p>
                <textarea
                  value={importJson}
                  onChange={(e) => setImportJson(e.target.value)}
                  rows={10}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 font-mono text-xs"
                  placeholder='[ { "title": "...", "content": "...", "image_ids": [], "created_at": "..." } ]'
                  data-testid="blog-import-textarea"
                />
                {importResult && (
                  <p className="text-sm mt-2 text-emerald-700 font-medium">{importResult}</p>
                )}
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={runImport}
                    disabled={importing || !importJson.trim()}
                    className="bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-800 disabled:opacity-50"
                    data-testid="blog-import-submit"
                  >
                    {importing ? "Importing…" : "Import Pasted JSON"}
                  </button>
                  <button type="button" onClick={() => { setShowImportForm(false); setImportJson(""); setImportResult(""); }} className="px-6 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Close</button>
                </div>
              </div>
            )}

            {showBlogForm && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="blog-form">
                <h3 className="font-bold text-lg mb-4">{editingBlog ? "Edit Blog Post" : "New Blog Post (Facebook Style)"}</h3>
                <p className="text-gray-500 text-sm mb-4">Paste your Facebook post text and upload the images. The post will appear in Facebook-style formatting on your website.</p>
                <form onSubmit={saveBlog} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Post Title *</label>
                      <input type="text" required value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500" data-testid="blog-title-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none" data-testid="blog-category-select">
                        {blogCategories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Post Content * (paste your Facebook text here)</label>
                    <textarea required value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} rows={10} className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Paste your Facebook post text here..." data-testid="blog-content-input" />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
                    <div className="flex flex-wrap gap-3 mb-3">
                      {blogForm.image_ids.map((imgId, idx) => (
                        <div key={imgId} className="relative group">
                          <img src={`${API}/images/${imgId}`} alt={`Upload ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border border-gray-200" />
                          <button type="button" onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors text-sm font-medium text-gray-700">
                      <Upload size={18} />
                      {uploadingImages ? "Uploading..." : "Upload Images"}
                      <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" disabled={uploadingImages} data-testid="blog-image-upload" />
                    </label>
                    <p className="text-xs text-gray-400 mt-1">Save images from Facebook first, then upload them here.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Facebook Post URL (optional)</label>
                    <input
                      type="url"
                      value={blogForm.facebook_url || ""}
                      onChange={(e) => setBlogForm({ ...blogForm, facebook_url: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="https://www.facebook.com/AuntieEducation/posts/..."
                      data-testid="blog-facebook-url-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">When set, visitors see "Like on Facebook" and "Share to Facebook" buttons on this post.</p>
                  </div>

                  <div className="flex gap-3">
                    <button type="submit" className="bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-800" data-testid="blog-save-btn">
                      {editingBlog ? "Update" : "Publish"}
                    </button>
                    <button type="button" onClick={() => { setShowBlogForm(false); setEditingBlog(null); }} className="px-6 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {blogPosts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500">
                <FileText className="mx-auto mb-4 text-gray-300" size={48} />
                <p className="text-lg">No blog posts yet. Click "New Blog Post" to paste your first Facebook article!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blogPosts.map((post) => (
                  <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-5 flex items-start justify-between gap-4" data-testid={`blog-item-${post.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium">{post.category}</span>
                        <span className="text-xs text-gray-400">{new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        {(post.image_ids?.length > 0) && <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Image size={12} /> {post.image_ids.length} image(s)</span>}
                      </div>
                      <h4 className="font-bold text-gray-900 truncate">{post.title}</h4>
                      <p className="text-gray-500 text-sm truncate">{post.content.substring(0, 120)}...</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => editBlog(post)} className="p-2 text-gray-400 hover:text-emerald-700 rounded-lg hover:bg-emerald-50" data-testid={`edit-blog-${post.id}`}><Pencil size={18} /></button>
                      <button onClick={() => deleteBlog(post.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50" data-testid={`delete-blog-${post.id}`}><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Resources Tab */}
        {activeTab === "resources" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Free Resources</h2>
              <button onClick={() => { setShowResourceForm(true); setEditingResource(null); setResourceForm({ title: "", category: "General", description: "", link: "" }); }} className="bg-emerald-700 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-800 inline-flex items-center gap-2" data-testid="add-resource-btn">
                <Plus size={18} /> New Resource
              </button>
            </div>

            {showResourceForm && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="resource-form">
                <h3 className="font-bold text-lg mb-4">{editingResource ? "Edit Resource" : "New Resource"}</h3>
                <form onSubmit={saveResource} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input type="text" required value={resourceForm.title} onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500" data-testid="resource-title-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select value={resourceForm.category} onChange={(e) => setResourceForm({ ...resourceForm, category: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none" data-testid="resource-category-select">
                        {resourceCategories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea required value={resourceForm.description} onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })} rows={4} className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500" data-testid="resource-desc-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link (optional)</label>
                    <input type="url" value={resourceForm.link} onChange={(e) => setResourceForm({ ...resourceForm, link: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-gray-200 outline-none" placeholder="https://..." data-testid="resource-link-input" />
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-800" data-testid="resource-save-btn">{editingResource ? "Update" : "Add Resource"}</button>
                    <button type="button" onClick={() => { setShowResourceForm(false); setEditingResource(null); }} className="px-6 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {resources.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500">
                <BookOpen className="mx-auto mb-4 text-gray-300" size={48} />
                <p className="text-lg">No resources yet. Click "New Resource" to add your first one!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {resources.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-5 flex items-start justify-between gap-4" data-testid={`resource-item-${item.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium">{item.category}</span>
                      </div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-gray-500 text-sm">{item.description.substring(0, 120)}</p>
                      {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-emerald-700 text-sm hover:underline mt-1 inline-block">{item.link}</a>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => editResource(item)} className="p-2 text-gray-400 hover:text-emerald-700 rounded-lg hover:bg-emerald-50"><Pencil size={18} /></button>
                      <button onClick={() => deleteResource(item.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === "inquiries" && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Student Inquiries</h2>
            {inquiries.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500">
                <Users className="mx-auto mb-4 text-gray-300" size={48} />
                <p className="text-lg">No inquiries yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inq) => (
                  <div key={inq.id} className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`inquiry-${inq.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-bold text-gray-900">{inq.student_name}</h4>
                          <select value={inq.status || "New"} onChange={(e) => updateInquiryStatus(inq.id, e.target.value)} className={`text-xs px-3 py-1 rounded-full font-medium border-0 outline-none cursor-pointer ${getStatusBadgeClasses(inq.status)}`} data-testid={`inquiry-status-${inq.id}`}>
                            <option value="New">New</option>
                            <option value="Contacted">Contacted</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-gray-600">
                          <p><strong>Parent/Guardian:</strong> {inq.parent_guardian_name}</p>
                          <p><strong>Email:</strong> <a href={`mailto:${inq.email}`} className="text-emerald-700 hover:underline">{inq.email}</a></p>
                          <p><strong>Phone:</strong> {inq.phone || "Not provided"}</p>
                          <p><strong>High School:</strong> {inq.high_school_name}</p>
                          <p><strong>Graduation:</strong> {inq.graduation_year}</p>
                          <p><strong>Address:</strong> {inq.city}, {inq.state} {inq.zip_code}</p>
                        </div>
                        {inq.additional_info && (
                          <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3"><strong>Notes:</strong> {inq.additional_info}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">Submitted: {new Date(inq.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div data-testid="analytics-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Site Analytics — Last 30 Days</h2>
              <button onClick={fetchAnalytics} className="text-sm text-emerald-700 hover:underline">Refresh</button>
            </div>
            {!analytics ? (
              <p className="text-gray-500">Loading analytics…</p>
            ) : (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Total Page Views</p>
                  <p className="text-5xl font-bold text-emerald-700 mt-2">{analytics.total_views.toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">Privacy-safe tracking — no IPs or personal data stored</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <AnalyticsCard title="Top Pages" rows={analytics.by_path} />
                  <AnalyticsCard title="Traffic Sources" rows={analytics.by_referrer} />
                  <AnalyticsCard title="Countries" rows={analytics.by_country} />
                  <AnalyticsCard title="Devices" rows={analytics.by_device} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
